import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Code,
  CopyButton,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useAgentTokens, useCreateAgentToken, useRevokeAgentToken, type AgentToken, type AgentTokenCreated } from '@/api/agentTokens';
import { errorMessage } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { SectionDivider } from '@/components/SectionDivider';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';

/** Tokens and OAuth grants through which an AI assistant acts as this user (docs/mcp-api.md §2). */
export function AssistantAccess({ isStaff }: { isStaff: boolean }) {
  const { t } = useTranslation();
  const { date, dateTime } = useFormat();
  const { data, isPending, isError, error } = useAgentTokens();
  const revoke = useRevokeAgentToken();
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<AgentTokenCreated | null>(null);
  const [toRevoke, setToRevoke] = useState<AgentToken | null>(null);

  const handleRevoke = async () => {
    if (!toRevoke) return;
    try {
      await revoke.mutateAsync(toRevoke.public_id);
      setToRevoke(null);
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  if (isPending) {
    return (
      <Center py="xl">
        <Loader color="forest" />
      </Center>
    );
  }
  if (isError) return <EmptyState title={errorMessage(error)} />;

  return (
    <Stack gap="lg">
      <Card>
        <Stack gap="xs">
          <Text>{t('assistant.intro')}</Text>
          <Group gap="xs">
            <Text fz={13} c={brand.textMuted}>
              {t('assistant.endpoint')}
            </Text>
            <Code>{data.mcp_url}</Code>
          </Group>
          <Text fz={13} c={brand.textMuted}>
            {t('assistant.connectorsHint')}
          </Text>
        </Stack>
      </Card>

      {created && <SecretPanel created={created} mcpUrl={data.mcp_url} onDone={() => setCreated(null)} />}

      <SectionDivider label={t('assistant.tokens')} />
      <Group justify="flex-end">
        <Button onClick={() => setCreateOpen(true)}>{t('assistant.newToken')}</Button>
      </Group>
      {data.tokens.length === 0 ? (
        <EmptyState title={t('assistant.none')} />
      ) : (
        <Card p={0}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('common.name')}</Table.Th>
                <Table.Th>{t('assistant.kind')}</Table.Th>
                <Table.Th>{t('assistant.scopes')}</Table.Th>
                <Table.Th>{t('assistant.created')}</Table.Th>
                <Table.Th>{t('assistant.lastUsed')}</Table.Th>
                <Table.Th>{t('assistant.expires')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.tokens.map((tok) => (
                <Table.Tr key={tok.public_id}>
                  <Table.Td>
                    <Text fw={600}>{tok.name}</Text>
                    {!tok.client && (
                      <Text fz={12} c={brand.textMuted}>
                        {tok.prefix}…
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{tok.client ? t('assistant.kindGrant') : t('assistant.kindToken')}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {tok.scopes.map((s) => (
                        <Badge key={s} variant="light" color="forest">
                          {t(`assistant.scope_${s}`)}
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>{date(tok.created_at)}</Table.Td>
                  <Table.Td>{tok.last_used_at ? dateTime(tok.last_used_at) : t('assistant.never')}</Table.Td>
                  <Table.Td>{tok.expires_at ? dateTime(tok.expires_at) : '—'}</Table.Td>
                  <Table.Td>
                    <Button variant="subtle" color="red" size="compact-sm" onClick={() => setToRevoke(tok)}>
                      {t('assistant.revoke')}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <CreateTokenModal
        opened={createOpen}
        isStaff={isStaff}
        onClose={() => setCreateOpen(false)}
        onCreated={(c) => {
          setCreateOpen(false);
          setCreated(c);
        }}
      />
      <Modal opened={!!toRevoke} onClose={() => setToRevoke(null)} title={t('assistant.revoke')}>
        <Stack gap="md">
          <Text>{t('assistant.revokeConfirm', { name: toRevoke?.name ?? '' })}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setToRevoke(null)}>
              {t('common.cancel')}
            </Button>
            <Button color="red" onClick={() => void handleRevoke()} loading={revoke.isPending}>
              {t('assistant.revoke')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function CreateTokenModal({
  opened,
  isStaff,
  onClose,
  onCreated,
}: {
  opened: boolean;
  isStaff: boolean;
  onClose: () => void;
  onCreated: (created: AgentTokenCreated) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateAgentToken();
  const [name, setName] = useState('');
  const [write, setWrite] = useState(false);
  const [expires, setExpires] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setWrite(false);
    setExpires('');
    setError(null);
  };
  const handleClose = () => {
    reset();
    onClose();
  };
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      const created = await create.mutateAsync({
        name,
        scopes: write ? ['read', 'write'] : ['read'],
        expires_at: expires ? new Date(expires).toISOString() : null,
      });
      reset();
      onCreated(created);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t('assistant.newToken')}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          <TextInput
            label={t('common.name')}
            placeholder={t('assistant.namePlaceholder')}
            required
            withAsterisk={false}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <TextInput
            label={t('assistant.expires')}
            description={t('assistant.expiresHint')}
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.currentTarget.value)}
          />
          {isStaff && (
            <Checkbox
              label={t('assistant.allowWrite')}
              description={t('assistant.allowWriteHint')}
              checked={write}
              onChange={(e) => setWrite(e.currentTarget.checked)}
            />
          )}
          {error && (
            <Text c="red" role="alert">
              {error}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t('assistant.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

/** The secret, shown once, with the ways to hand it to an assistant. */
function SecretPanel({ created, mcpUrl, onDone }: { created: AgentTokenCreated; mcpUrl: string; onDone: () => void }) {
  const { t } = useTranslation();
  const claudeCode = `claude mcp add --transport http forestum ${mcpUrl} --header "Authorization: Bearer ${created.secret}"`;
  return (
    <Card style={{ borderColor: brand.yellow }} withBorder>
      <Stack gap="sm">
        <Title order={4}>{t('assistant.secretTitle', { name: created.name })}</Title>
        <Text fz={13} c={brand.textMuted}>
          {t('assistant.secretOnce')}
        </Text>
        <CopyRow label={t('assistant.secret')} value={created.secret} />
        <CopyRow label={t('assistant.claudeCode')} value={claudeCode} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onDone}>
            {t('assistant.done')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  return (
    <Stack gap={4}>
      <Text fz={12} fw={600} c={brand.textMuted}>
        {label}
      </Text>
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <Code block style={{ flex: 1, wordBreak: 'break-all' }}>
          {value}
        </Code>
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Button size="compact-sm" variant="default" onClick={copy}>
              {copied ? t('assistant.copied') : t('assistant.copy')}
            </Button>
          )}
        </CopyButton>
      </Group>
    </Stack>
  );
}
